/**
 * GroupsTab - Unified Groups view for View Campaign
 * 
 * Displays all campaign groups in a single unified view.
 * 
 * Key principle: Campaigns are always the source of truth.
 * Packs are creation-time templates only — there is no live pack linkage.
 * 
 * All groups are shown as "Editable Groups" regardless of origin (pack-derived or manual).
 * Editing is done via Detailed Edit.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiJson } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Package, Users, Layers, Info } from 'lucide-react';
import type { UnifiedGroup, GroupsViewResponse } from '@shared/unified-groups';

interface GroupsTabProps {
  campaignId: number;
}

export function GroupsTab({ campaignId }: GroupsTabProps) {
  const { data, isLoading, error } = useQuery<GroupsViewResponse>({
    queryKey: ['/api/campaigns', campaignId, 'groups-view'],
    queryFn: async () => {
      return apiJson(`/api/campaigns/${campaignId}/groups-view`);
    },
    enabled: !!campaignId,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2 text-muted-foreground">Loading groups...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <div className="mb-2">Error loading groups</div>
        <div className="text-sm">{error instanceof Error ? error.message : 'Unknown error'}</div>
      </div>
    );
  }

  // Combine all groups into a single unified list
  // Both authored groups and derived groups are treated identically
  const authoredGroups = data?.authoredGroups || [];
  const derivedGroups = data?.derivedGroups || [];
  const allGroups = [...authoredGroups, ...derivedGroups];
  const hasGroups = allGroups.length > 0;

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5" />
                Groups Overview
              </CardTitle>
              <CardDescription>
                {allGroups.length} group{allGroups.length !== 1 ? 's' : ''} in this campaign
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{allGroups.length}</div>
              <div className="text-sm text-muted-foreground">Total Groups</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{data?.debug?.storeCount ?? 0}</div>
              <div className="text-sm text-muted-foreground">Unique Stores</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{data?.debug?.formatCount ?? 0}</div>
              <div className="text-sm text-muted-foreground">Formats</div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className="text-2xl font-bold">{data?.debug?.totalQuantity ?? 0}</div>
              <div className="text-sm text-muted-foreground">Total Quantity</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Unified Editable Groups Section */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Package className="h-4 w-4" />
            Editable Groups
          </CardTitle>
          <CardDescription className="text-xs">
            Campaign structure. Edit via Detailed Edit to modify stores, formats, operators, or quantities.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {hasGroups ? (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {allGroups.map((group) => (
                <GroupCard key={group.groupKey} group={group} />
              ))}
            </div>
          ) : (
            <div className="text-center py-6 text-muted-foreground bg-muted/30 rounded-lg">
              <Package className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <div className="text-sm font-medium">No groups defined</div>
              <div className="text-xs mt-1">
                This campaign has no group structure defined.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface GroupCardProps {
  group: UnifiedGroup;
}

function GroupCard({ group }: GroupCardProps) {
  const isPackDerived = group.seedPackId != null;
  
  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-lg line-clamp-2">{group.groupName}</CardTitle>
        </div>
        {/* Subtle provenance indicator - informational only */}
        {isPackDerived && group.seedPackName && (
          <CardDescription className="text-xs flex items-center gap-1 text-muted-foreground">
            <Info className="h-3 w-3" />
            Originally from: {group.seedPackName}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-1 space-y-4">
        {/* Store count */}
        <div className="flex items-center gap-2 text-sm">
          <Users className="h-4 w-4 text-muted-foreground" />
          <span>{group.storeIds.length} {group.storeIds.length === 1 ? 'store' : 'stores'}</span>
        </div>
        
        {/* Format entries */}
        {group.formatEntries.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-muted-foreground">Formats</div>
            <div className="space-y-1">
              {group.formatEntries.map((entry) => (
                <div 
                  key={entry.mediaFormatId}
                  className="flex items-center justify-between text-sm bg-muted/50 rounded px-2 py-1"
                >
                  <span className="truncate mr-2">
                    {entry.mediaFormatName || `Format ${entry.mediaFormatId}`}
                  </span>
                  <Badge variant="outline" className="shrink-0">
                    {entry.quantity}
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
